import { ApiError } from '@/lib/api'
import type { Format, JobDetail, Recipe } from '@/lib/scraper'
import { FINAL_STATUSES, scraperApi, useJobEvents } from '@/lib/scraper'
import { useQuery } from '@tanstack/react-query'
import { Globe, Loader2, Radar, Sparkles, Wand2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { JobProgress } from './JobProgress'
import { JsonBlock, ResultView } from './ResultView'
import {
  BTN_PRIMARY,
  CARD,
  CHIP_OFF,
  CHIP_ON,
  INPUT,
  LABEL,
  MUTED,
  SKELETON,
  TEXTAREA,
  cls
} from './ui'

type Mode = 'scrape' | 'crawl' | 'map' | 'extract'

const MODES: { id: Mode; label: string; hint: string; icon: typeof Globe }[] = [
  {
    id: 'scrape',
    label: 'Scrape',
    hint: 'One page, every format',
    icon: Globe
  },
  {
    id: 'crawl',
    label: 'Crawl',
    hint: 'A whole site, page by page',
    icon: Radar
  },
  { id: 'map', label: 'Map', hint: 'List every URL on a site', icon: Sparkles },
  {
    id: 'extract',
    label: 'Extract',
    hint: 'Structured JSON from a list of URLs',
    icon: Wand2
  }
]
const FORMATS: Format[] = [
  'markdown',
  'screenshot',
  'sections',
  'links',
  'metadata',
  'text',
  'html',
  'raw_html'
]
const RECIPES: Recipe[] = ['business', 'menu', 'products', 'events']

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.detail || err.message
  return err instanceof Error ? err.message : 'Something went wrong'
}

export function Playground({ onOpenJob }: { onOpenJob: (id: string) => void }) {
  const [mode, setMode] = useState<Mode>('scrape')
  const [url, setUrl] = useState('')
  const [urls, setUrls] = useState('')
  const [formats, setFormats] = useState<Format[]>([
    'markdown',
    'screenshot',
    'sections',
    'links',
    'metadata'
  ])
  const [targets, setTargets] = useState<Recipe[]>(['business'])
  const [vision, setVision] = useState(false)
  const [onlyMain, setOnlyMain] = useState(true)
  const [followInfo, setFollowInfo] = useState(true)
  const [limit, setLimit] = useState(25)
  const [depth, setDepth] = useState(2)
  const [robotsOverride, setRobotsOverride] = useState(false)
  const [schema, setSchema] = useState('')
  const [prompt, setPrompt] = useState('')
  const [search, setSearch] = useState('')
  const [pending, setPending] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  const [mapUrls, setMapUrls] = useState<string[] | null>(null)

  const events = useJobEvents(jobId)
  const job = useQuery({
    queryKey: ['scraper-job', jobId],
    queryFn: () => scraperApi.job(jobId as string),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const data = q.state.data as JobDetail | undefined
      return data && FINAL_STATUSES.includes(data.status) ? false : 2000
    }
  })

  const toggle = <T,>(list: T[], value: T, set: (next: T[]) => void) =>
    set(
      list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
    )

  const parsedSchema = (): Record<string, unknown> | null | 'invalid' => {
    if (!schema.trim()) return null
    try {
      return JSON.parse(schema) as Record<string, unknown>
    } catch {
      return 'invalid'
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const customSchema = parsedSchema()
    if (customSchema === 'invalid') {
      toast.error('The JSON schema is not valid JSON')
      return
    }
    setPending(true)
    setMapUrls(null)
    setJobId(null)
    try {
      if (mode === 'scrape') {
        const res = await scraperApi.scrape({
          url,
          formats:
            customSchema || targets.length ? [...formats, 'json'] : formats,
          targets,
          vision,
          only_main_content: onlyMain,
          follow_info_pages: followInfo,
          schema: customSchema,
          prompt
        })
        setJobId(res.job_id)
        if (res.status === 'failed')
          toast.error(res.error || 'The scrape failed')
      } else if (mode === 'crawl') {
        const res = await scraperApi.crawl({
          url,
          limit,
          max_depth: depth,
          formats: formats.filter((f) => f !== 'raw_html'),
          targets,
          vision,
          schema: customSchema,
          prompt,
          robots_override: robotsOverride
        })
        setJobId(res.job_id)
        toast.success('Crawl started')
      } else if (mode === 'map') {
        const res = await scraperApi.map({ url, search })
        setJobId(res.job_id)
        if (res.urls) setMapUrls(res.urls)
      } else {
        const list = urls
          .split(/\s+/)
          .map((u) => u.trim())
          .filter(Boolean)
        if (!list.length) {
          toast.error('Add at least one URL')
          return
        }
        const res = await scraperApi.extract({
          urls: list,
          targets,
          schema: customSchema,
          prompt,
          vision,
          follow_info_pages: followInfo
        })
        setJobId(res.job_id)
        toast.success('Extraction started')
      }
    } catch (err) {
      toast.error(errorText(err))
    } finally {
      setPending(false)
    }
  }

  const detail = job.data
  const isFinal = detail ? FINAL_STATUSES.includes(detail.status) : false
  const scrapeResult =
    mode === 'scrape' && detail?.result && !detail.result.urls
      ? detail.result
      : null
  const extractions = scrapeResult?.json
    ? Object.values(scrapeResult.json)
    : (detail?.extractions ?? [])
  const urlsFromMap =
    mapUrls ?? (mode === 'map' ? (detail?.result?.urls ?? null) : null)

  return (
    <div className="space-y-6">
      <form onSubmit={submit} className={cls(CARD, 'space-y-5')}>
        <div
          role="tablist"
          aria-label="Mode"
          className="grid grid-cols-2 gap-2 sm:grid-cols-4"
        >
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={mode === m.id}
              onClick={() => setMode(m.id)}
              className={cls(
                'flex flex-col items-start gap-1 rounded-[24px] border p-3 text-left transition-colors',
                mode === m.id
                  ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-black'
                  : 'border-neutral-200 bg-white text-neutral-900 hover:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:hover:border-neutral-600'
              )}
            >
              <m.icon className="size-4" />
              <span className="text-sm font-bold">{m.label}</span>
              <span
                className={cls(
                  'text-[11px]',
                  mode === m.id
                    ? 'opacity-70'
                    : 'text-neutral-500 dark:text-neutral-400'
                )}
              >
                {m.hint}
              </span>
            </button>
          ))}
        </div>

        {mode === 'extract' ? (
          <div>
            <label htmlFor="scraper-urls" className={LABEL}>
              URLs (one per line, up to 50)
            </label>
            <textarea
              id="scraper-urls"
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              placeholder={'https://example.com/\nhttps://example.com/menu'}
              className={TEXTAREA}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row">
            <label htmlFor="scraper-url" className="sr-only">
              URL
            </label>
            <input
              id="scraper-url"
              type="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://any-business-website.com"
              className={cls(INPUT, 'text-base')}
            />
            <button
              type="submit"
              disabled={pending}
              className={cls(BTN_PRIMARY, 'sm:w-40')}
            >
              {pending ? <Loader2 className="size-4 animate-spin" /> : null}
              {pending
                ? mode === 'scrape'
                  ? 'Reading…'
                  : 'Starting…'
                : MODES.find((m) => m.id === mode)?.label}
            </button>
          </div>
        )}

        {mode !== 'map' && (
          <div className="grid gap-5 md:grid-cols-2">
            {mode !== 'extract' && (
              <fieldset>
                <legend className={LABEL}>Formats</legend>
                <div className="flex flex-wrap gap-2">
                  {FORMATS.filter(
                    (f) => mode === 'scrape' || f !== 'raw_html'
                  ).map((f) => (
                    <button
                      key={f}
                      type="button"
                      aria-pressed={formats.includes(f)}
                      onClick={() => toggle(formats, f, setFormats)}
                      className={formats.includes(f) ? CHIP_ON : CHIP_OFF}
                    >
                      {f.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}
            <fieldset>
              <legend className={LABEL}>Structured data (recipes)</legend>
              <div className="flex flex-wrap gap-2">
                {RECIPES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={targets.includes(r)}
                    onClick={() => toggle(targets, r, setTargets)}
                    className={targets.includes(r) ? CHIP_ON : CHIP_OFF}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </fieldset>
          </div>
        )}

        {mode === 'map' && (
          <div>
            <label htmlFor="scraper-search" className={LABEL}>
              Only URLs containing (optional)
            </label>
            <input
              id="scraper-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="menu"
              className={INPUT}
            />
          </div>
        )}

        {mode === 'crawl' && (
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="scraper-limit" className={LABEL}>
                Page limit (max 2000)
              </label>
              <input
                id="scraper-limit"
                type="number"
                min={1}
                max={2000}
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className={INPUT}
              />
            </div>
            <div>
              <label htmlFor="scraper-depth" className={LABEL}>
                Max depth
              </label>
              <input
                id="scraper-depth"
                type="number"
                min={0}
                max={10}
                value={depth}
                onChange={(e) => setDepth(Number(e.target.value))}
                className={INPUT}
              />
            </div>
            <Toggle
              id="scraper-robots"
              label="Ignore robots.txt"
              checked={robotsOverride}
              onChange={setRobotsOverride}
              hint="Recorded on the job"
            />
          </div>
        )}

        {mode !== 'map' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Toggle
              id="scraper-vision"
              label="Force vision read"
              checked={vision}
              onChange={setVision}
              hint="Screenshot tiles read by the vision model; uses the daily vision budget"
            />
            {mode === 'scrape' && (
              <Toggle
                id="scraper-main"
                label="Main content only"
                checked={onlyMain}
                onChange={setOnlyMain}
                hint="Drops navigation, footer and cookie banners from markdown"
              />
            )}
            {(mode === 'scrape' || mode === 'extract') &&
              targets.includes('business') && (
                <Toggle
                  id="scraper-follow"
                  label="Complete basic info"
                  checked={followInfo}
                  onChange={setFollowInfo}
                  hint="If address, hours, phone or category are missing, read up to 3 contact, location or about pages found in the sitemap"
                />
              )}
          </div>
        )}

        {mode !== 'map' && (
          <details className="group">
            <summary className="cursor-pointer text-xs font-semibold text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white">
              Custom JSON schema (optional)
            </summary>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div>
                <label htmlFor="scraper-schema" className={LABEL}>
                  JSON schema (top level object)
                </label>
                <textarea
                  id="scraper-schema"
                  value={schema}
                  onChange={(e) => setSchema(e.target.value)}
                  placeholder={
                    '{"type":"object","properties":{"owner_name":{"type":"string"}}}'
                  }
                  className={TEXTAREA}
                />
              </div>
              <div>
                <label htmlFor="scraper-prompt" className={LABEL}>
                  Prompt
                </label>
                <textarea
                  id="scraper-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Who owns this business?"
                  className={cls(TEXTAREA, 'font-sans')}
                />
              </div>
            </div>
          </details>
        )}

        {mode === 'extract' && (
          <button type="submit" disabled={pending} className={BTN_PRIMARY}>
            {pending ? <Loader2 className="size-4 animate-spin" /> : null}
            Extract
          </button>
        )}
      </form>

      {pending && mode === 'scrape' && !jobId && <PendingSkeleton />}

      {jobId && detail && (
        <JobProgress
          job={detail}
          events={events}
          onOpen={mode !== 'scrape' ? () => onOpenJob(jobId) : undefined}
        />
      )}

      {scrapeResult?.followed_pages &&
        scrapeResult.followed_pages.length > 0 && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Basic info completed from: {scrapeResult.followed_pages.join(', ')}
          </p>
        )}
      {scrapeResult && (
        <ResultView page={scrapeResult} extractions={extractions} />
      )}
      {mode === 'scrape' &&
        jobId &&
        isFinal &&
        !scrapeResult &&
        detail?.status !== 'completed' && (
          <p className="text-sm text-red-500">
            {detail?.error || 'The scrape did not return a result.'}
          </p>
        )}
      {urlsFromMap && (
        <div className={CARD}>
          <h3 className="mb-3 text-sm font-semibold text-neutral-900 dark:text-white">
            {urlsFromMap.length} URLs
          </h3>
          {urlsFromMap.length === 0 ? (
            <p className={MUTED}>No URLs found.</p>
          ) : (
            <JsonBlock value={urlsFromMap} />
          )}
        </div>
      )}
      {mode !== 'scrape' &&
        mode !== 'map' &&
        isFinal &&
        extractions.length > 0 && (
          <div className="space-y-4">
            <ResultView
              page={{ url: detail?.url ?? '' }}
              extractions={extractions}
            />
          </div>
        )}
    </div>
  )
}

function Toggle({
  id,
  label,
  hint,
  checked,
  onChange
}: {
  id: string
  label: string
  hint?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-[24px] border border-neutral-200 p-3 dark:border-neutral-800"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 accent-neutral-900 dark:accent-white"
      />
      <span>
        <span className="block text-sm font-semibold text-neutral-900 dark:text-white">
          {label}
        </span>
        {hint && <span className={MUTED}>{hint}</span>}
      </span>
    </label>
  )
}

function PendingSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-live="polite">
      <div className={cls(SKELETON, 'h-14 w-full')} />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className={cls(SKELETON, 'h-[420px]')} />
        <div className={cls(SKELETON, 'h-[420px]')} />
      </div>
    </div>
  )
}
